import { message } from 'ant-design-vue'
import type { Ref, ShallowRef } from 'vue'
import { onUnmounted, reactive, ref, shallowRef, toRefs } from 'vue'

interface UseFetchOptions<T> {
  /** 获取数据的异步函数 */
  getFn: (params?: any) => Promise<T>
  /** 获取数据成功后的回调函数 */
  afterGetFn?: (data: T, params?: any) => void
  /** 自定义错误处理回调函数，如果返回 true 则阻止默认的错误提示 */
  onError?: (error: any, params?: any) => boolean | void
  /** 获取失败时的错误提示信息 */
  errMsg?: string
  /** 是否使用 shallowRef */
  isShallow?: boolean
  /** 数据的默认值 */
  defaultValue?: T
  /** 是否在请求失败时显示错误提示，默认为 true */
  showErrorMessage?: boolean
  /** 是否在组件卸载时自动取消请求，默认为 true */
  cancelOnUnmount?: boolean
}

/**
 * 请求状态接口
 */
interface FetchState {
  /** 是否正在加载中 */
  loading: boolean
  /** 是否已经成功加载过数据 */
  loaded: boolean
  /** 是否加载出错 */
  loadError: boolean
}

/**
 * useFetch 返回值类型
 * @template T - 返回数据的类型
 */
interface UseFetchReturn<T> {
  /** 数据结果（响应式） */
  result: Ref<T | undefined> | ShallowRef<T | undefined>
  /** 是否正在加载 */
  loading: Ref<boolean>
  /** 是否已成功加载 */
  loaded: Ref<boolean>
  /** 是否加载出错 */
  loadError: Ref<boolean>
  /** 获取数据的方法 */
  fetchData: (params?: any) => Promise<T | undefined>
  /** 刷新数据（使用上次的参数重新请求） */
  refresh: () => Promise<T | undefined>
  /** 重置状态到初始值 */
  reset: () => void
  /** 手动开启 loading 状态 */
  openLoading: () => void
  /** 手动关闭 loading 状态 */
  closeLoading: () => void
  /** 取消当前请求 */
  cancel: () => void
}

/**
 * 通用的数据获取 Hook
 *
 * @example
 * ```ts
 * const { result, loading, fetchData, refresh } = useFetch<UserInfo>({
 *   getFn: (params) => getUserInfo(params),
 *   afterGetFn: (data) => console.log('获取成功', data),
 *   defaultValue: { name: '', age: 0 }
 * })
 *
 * // 获取数据
 * await fetchData({ userId: 1 })
 *
 * // 使用相同参数刷新
 * await refresh()
 * ```
 */
export function useFetch<T = any>(options: UseFetchOptions<T>): UseFetchReturn<T> {
  const {
    getFn,
    afterGetFn,
    onError,
    errMsg = '获取失败',
    isShallow = false,
    defaultValue,
    showErrorMessage = true,
    cancelOnUnmount = true
  } = options

  const result = isShallow ? shallowRef<T | undefined>(defaultValue) : (ref<T | undefined>(defaultValue) as Ref<T | undefined>)

  // 请求状态管理
  const state = reactive<FetchState>({
    loading: false,
    loaded: false,
    loadError: false
  })

  // 保存上次请求的参数，用于刷新功能
  let lastParams: any = undefined

  // 请求取消标志
  let isCancelled = false

  // 当前请求的版本号，用于处理竞态条件
  let requestVersion = 0

  const openLoading = (): void => {
    state.loading = true
  }

  const closeLoading = (): void => {
    state.loading = false
  }

  const reset = (): void => {
    state.loading = false
    state.loaded = false
    state.loadError = false
    result.value = defaultValue
    lastParams = undefined
    isCancelled = false
  }

  const cancel = (): void => {
    isCancelled = true
    closeLoading()
  }

  /**
   * 获取数据
   */
  const fetchData = async (params?: any): Promise<T | undefined> => {
    if (state.loading) return

    // 保存参数用于刷新
    lastParams = params

    // 重置取消标志和错误状态
    isCancelled = false
    state.loadError = false

    // 增加请求版本号，用于处理竞态条件
    const currentVersion = ++requestVersion

    openLoading()

    try {
      const data = await getFn(params)

      // 检查请求是否已被取消或是否为过期请求
      if (isCancelled || currentVersion !== requestVersion) {
        console.warn('请求已被取消或已过期')
        return
      }

      result.value = data
      state.loaded = true
      afterGetFn?.(data, params)
      return data
    } catch (err) {
      if (isCancelled || currentVersion !== requestVersion) return

      // 记录错误信息
      state.loadError = true

      console.error('请求失败:', err)

      // 调用自定义错误处理回调
      const preventDefaultMessage = onError?.(err, params)

      // 如果自定义处理返回 true，则阻止默认错误提示；否则根据配置决定是否显示
      if (!preventDefaultMessage && showErrorMessage) message.error(errMsg)

      return
    } finally {
      // 确保 loading 状态被关闭（仅当请求未被取消时）
      if (!isCancelled && currentVersion === requestVersion) {
        closeLoading()
      }
    }
  }

  /**
   * 使用上次的参数刷新数据
   */
  const refresh = async (): Promise<T | undefined> => {
    state.loading = false
    return fetchData(lastParams)
  }

  // 组件卸载时自动取消请求
  if (cancelOnUnmount) {
    onUnmounted(() => {
      cancel()
    })
  }

  return {
    result,
    ...toRefs(state),
    fetchData,
    refresh,
    reset,
    openLoading,
    closeLoading,
    cancel
  }
}
